import React, { useEffect, useState } from 'react';
import { cn } from '../../../utils/cn';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { apiGet, apiPost } from '../../../lib/shopperApi';

const deviceIconFor = (ua = '') => {
  const s = ua.toLowerCase();
  if (s.includes('iphone') || s.includes('android')) return 'Smartphone';
  if (s.includes('ipad') || s.includes('tablet'))    return 'Tablet';
  return 'Monitor';
};

const summarizeUA = (ua = '') => {
  if (!ua) return 'Unknown device';
  const s = ua;
  // Browser
  let browser = 'Browser';
  if (/Edg\//i.test(s))       browser = 'Edge';
  else if (/OPR\/|Opera/i.test(s)) browser = 'Opera';
  else if (/Chrome\//i.test(s) && !/Edg\//i.test(s)) browser = 'Chrome';
  else if (/Firefox\//i.test(s))   browser = 'Firefox';
  else if (/Safari\//i.test(s))    browser = 'Safari';
  // OS
  let os = '';
  if      (/iPhone|iOS/i.test(s)) os = 'iPhone';
  else if (/iPad/i.test(s))        os = 'iPad';
  else if (/Android/i.test(s))     os = 'Android';
  else if (/Mac OS X/i.test(s))    os = 'macOS';
  else if (/Windows/i.test(s))     os = 'Windows';
  else if (/Linux/i.test(s))       os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
};

const fmtWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.replace(' ', 'T') + (iso.includes('T') ? '' : 'Z'));
  if (isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString()} at ${d.toLocaleTimeString()}`;
};

const SecuritySettings = () => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg]   = useState(null);
  const [err, setErr]   = useState(null);

  const [sessions, setSessions] = useState(null);
  const [sessErr, setSessErr]   = useState(null);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const loadSessions = async () => {
    const { ok, data } = await apiGet('/sessions');
    if (!ok) { setSessErr(data?.error || 'Could not load sessions.'); setSessions([]); return; }
    setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    setSessErr(null);
  };

  useEffect(() => { loadSessions(); }, []);

  const validate = () => {
    if (!form.current) return 'Enter your current password.';
    if (form.next.length < 8) return 'New password must be at least 8 characters.';
    if (!/[a-z]/.test(form.next) || !/[A-Z]/.test(form.next) || !/[0-9]/.test(form.next)) {
      return 'New password needs at least one uppercase letter, one lowercase letter, and one number.';
    }
    if (form.next !== form.confirm) return 'New password and confirmation do not match.';
    return null;
  };

  const submit = async () => {
    const v = validate();
    if (v) { setErr(v); setMsg(null); return; }
    setBusy(true); setErr(null); setMsg(null);
    const { ok, data } = await apiPost('/set-password', {
      current_password: form.current,
      new_password: form.next,
    });
    setBusy(false);
    if (!ok) { setErr(data?.error || 'Could not update password.'); return; }
    setMsg(data?.message || 'Password updated. Other devices have been signed out.');
    setForm({ current: '', next: '', confirm: '' });
    setShowForm(false);
    loadSessions();
  };

  const revoke = async (id) => {
    if (!window.confirm('Sign this device out?')) return;
    const { ok, data } = await apiPost(`/sessions/${id}/revoke`, {});
    if (!ok) { setSessErr(data?.error || 'Could not revoke session.'); return; }
    if (Array.isArray(data?.sessions)) setSessions(data.sessions);
  };

  return (
    <div className="max-w-4xl space-y-6">
      {/* Password ────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Password</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Change the password for your Bounderlaster account.
              </p>
            </div>
            {!showForm && (
              <Button
                variant="outline"
                onClick={() => { setShowForm(true); setMsg(null); setErr(null); }}
                iconName="Key"
                iconPosition="left"
              >
                Change Password
              </Button>
            )}
          </div>
        </div>

        {(msg || err) && (
          <div className="px-6 pt-4">
            {msg && <p className="text-sm text-green-700">{msg}</p>}
            {err && <p className="text-sm text-red-700">{err}</p>}
          </div>
        )}

        {showForm && (
          <div className="px-6 py-4 bg-accent/10 border-t border-border">
            <div className="max-w-md space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Current Password</label>
                <Input type="password" value={form.current} onChange={(e) => setField('current', e.target.value)} placeholder="Enter current password" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">New Password</label>
                <Input type="password" value={form.next} onChange={(e) => setField('next', e.target.value)} placeholder="At least 8 chars, mixed case & a number" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Confirm New Password</label>
                <Input type="password" value={form.confirm} onChange={(e) => setField('confirm', e.target.value)} placeholder="Re-enter new password" />
              </div>
              <div className="flex items-center space-x-3 pt-2">
                <Button onClick={submit} iconName="Check" iconPosition="left" size="sm" disabled={busy}>
                  {busy ? 'Updating…' : 'Update Password'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setShowForm(false); setForm({ current: '', next: '', confirm: '' }); setErr(null); }}
                  disabled={busy}
                >
                  Cancel
                </Button>
              </div>
              <p className="text-xs text-muted-foreground pt-2">
                Updating your password signs out any other devices currently logged in.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Active sessions ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-border">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Active Sessions</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Devices currently signed into your account. Sign out any you don't recognize.
          </p>
        </div>

        {sessErr && <div className="px-6 pt-4"><p className="text-sm text-red-700">{sessErr}</p></div>}

        <div className="divide-y divide-border">
          {sessions === null ? (
            <div className="p-6 text-sm text-muted-foreground">Loading sessions…</div>
          ) : sessions.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No active sessions.</div>
          ) : sessions.map((s) => (
            <div key={s.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-accent/20 rounded-lg flex items-center justify-center">
                    <Icon name={deviceIconFor(s.user_agent)} size={20} className="text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="font-medium text-foreground">{summarizeUA(s.user_agent)}</p>
                      {s.current && (
                        <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          This device
                        </span>
                      )}
                    </div>
                    {s.ip_address && (
                      <p className="text-sm text-muted-foreground mb-1">IP {s.ip_address}</p>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Last active: {fmtWhen(s.last_used_at)}
                    </p>
                  </div>
                </div>

                {!s.current && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => revoke(s.id)}
                    iconName="LogOut"
                    className="text-red-600 hover:text-red-700"
                  >
                    Sign Out
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-border p-6">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-primary mt-0.5" />
          <div>
            <p className="font-medium text-foreground">Account safety</p>
            <p className="text-sm text-muted-foreground">
              We never share your password or order details. If you spot a session you don't recognize,
              sign it out here and change your password right away.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
